'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Pillows and Mattresses: What You Breathe All Night"
      author="MicroPlastics Editorial Team"
      date="March 12, 2025"
      readTime="10 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in pillows and mattresses, synthetic fibres and foam"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        You spend roughly <strong>a third of your life</strong> on a mattress
        and pillow, with your face often inches from the fabric. Conventional
        mattresses are <strong>polyurethane foam</strong> with synthetic fibre
        ticking; conventional pillows are <strong>polyester fibre fill</strong>
        or memory foam. Both shed microplastic fibres into bedroom air,
        release flame retardants (brominated and chlorinated chemicals), and
        off-gas VOCs for years. The safer alternatives: <strong>natural latex
        (Avocado, Naturepedic), organic cotton or wool mattresses (Birch,
        Saatva Latex Hybrid), buckwheat hull or wool pillows</strong>, all
        certified GOTS, GOLS, or GREENGUARD Gold.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Polyurethane foam mattresses are 100% plastic foam, and many contain brominated and chlorinated flame retardants.</>,
          <>Memory foam mattresses release VOCs (volatile organic compounds) for years; new-mattress smell = chemical off-gassing.</>,
          <>Polyester pillow fill sheds microplastic fibres directly into your breathing zone for 8+ hours a night.</>,
          <>The 2017 Dris et al. study found textile fibres dominate indoor microplastic air load, bedrooms are the worst-exposure room.</>,
          <>Safer: natural latex (Avocado, Naturepedic, Birch), organic wool mattresses, buckwheat hull pillows, organic cotton-encased innerspring. Look for GOTS, GOLS, GREENGUARD Gold, or MADE SAFE certifications.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why bedrooms are the worst microplastic-exposure room
      </h2>
      <p>
        You sleep ~7-9 hours a night with your face <strong>inches from
        textile surfaces</strong>. Your breathing draws air directly through
        fibres being released from:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Pillows</strong>, polyester fibre fill (most common), memory foam, or down with synthetic ticking.</li>
        <li><strong>Mattresses</strong>, polyurethane foam core, polyester quilting, synthetic ticking.</li>
        <li><strong>Pillowcases and sheets</strong>, polyester or polyester-cotton blends.</li>
        <li><strong>Mattress protector</strong>, typically polyester with PVC waterproof backing.</li>
        <li><strong>Carpet</strong> if your bedroom has it, synthetic nylon or polyester.</li>
        <li><strong>Curtains</strong>, most are polyester.</li>
      </ul>
      <p>
        Combined, this creates a high-microplastic environment for the longest
        single-location daily exposure of your life.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What conventional mattresses are made of
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Polyurethane foam (most common)</strong>, petroleum-derived;
          off-gasses VOCs for months to years; contains flame retardants in many products.
        </li>
        <li>
          <strong>Memory foam (viscoelastic polyurethane)</strong>, same base
          chemistry as polyurethane, plus additional chemical modifications.
        </li>
        <li>
          <strong>Polyester fibre quilting and pillowtop</strong>, sheds microfibres.
        </li>
        <li>
          <strong>Synthetic ticking (mattress cover)</strong>, usually polyester.
        </li>
        <li>
          <strong>Flame retardants</strong>, brominated (BFRs) and chlorinated
          chemicals required by US flammability standards (16 CFR 1633). Many
          have documented thyroid disruption.
        </li>
        <li>
          <strong>Antimicrobial treatments</strong>, sometimes triclosan
          (regulated/restricted) or silver compounds.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Pillow fill compared
      </h2>

      <BlogComparisonTable
        caption="Pillow fill materials ranked for microplastic safety"
        columns={[
          { key: 'fill', label: 'Fill material' },
          { key: 'safety', label: 'Microplastic safety' },
          { key: 'notes', label: 'Notes' },
        ]}
        rows={[
          { fill: 'Buckwheat hulls', safety: 'Zero', notes: 'Firm, traditional; adjustable; lasts 10+ years' },
          { fill: 'Organic wool', safety: 'Zero', notes: 'Naturally flame-retardant; regulates temperature' },
          { fill: 'Organic kapok (silk floss tree)', safety: 'Zero', notes: 'Soft, vegan, hypoallergenic' },
          { fill: 'Organic cotton', safety: 'Zero', notes: 'Hot sleepers may find too warm' },
          { fill: 'Natural latex (shredded)', safety: 'Very low', notes: 'Plant-derived rubber; verify 100% natural latex (not blend)' },
          { fill: 'Down (ethically sourced)', safety: 'Low', notes: 'Verify down ticking is organic cotton (not polyester)' },
          { fill: 'Memory foam (polyurethane)', safety: 'Moderate-high', notes: 'Off-gasses VOCs; flame retardants common' },
          { fill: 'Polyester fibre fill (most common)', safety: 'Highest', notes: 'Sheds microfibres into breathing zone all night' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Recommended pillow brands (2025-2026)
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Hullo Buckwheat Pillows</strong>. 100% buckwheat hulls in organic cotton casing. $89-119.</li>
        <li><strong>Sleep &amp; Beyond Wool Pillow</strong>, organic wool in organic cotton casing. $80-150.</li>
        <li><strong>Avocado Green Pillow</strong>, shredded GOLS-certified natural latex + kapok. $79-129.</li>
        <li><strong>Holy Lamb Organics Pillow</strong>, organic wool. $90-160.</li>
        <li><strong>Naturepedic Organic Cotton Pillow</strong>, kapok or shredded latex options. $75-150.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Recommended mattress brands (2025-2026)
      </h2>

      <BlogComparisonTable
        caption="Microplastic-conscious mattresses ranked"
        columns={[
          { key: 'brand', label: 'Brand / Model' },
          { key: 'material', label: 'Material' },
          { key: 'cert', label: 'Certification' },
          { key: 'price', label: 'Price (Queen)' },
        ]}
        rows={[
          { brand: 'Avocado Green', material: 'Natural latex + organic cotton + organic wool', cert: 'GOLS, GOTS, GREENGUARD Gold', price: '$1,599-2,400' },
          { brand: 'Naturepedic EOS Classic', material: 'Organic cotton + organic wool + natural latex + encased coils', cert: 'GOTS, GOLS, GREENGUARD Gold, MADE SAFE', price: '$2,999-3,999' },
          { brand: 'Birch by Helix', material: 'Organic cotton + organic wool + natural latex + steel coils', cert: 'GREENGUARD Gold, eco-INSTITUT, fair trade wool', price: '$1,399-1,899' },
          { brand: 'Saatva Latex Hybrid', material: 'Talalay latex + organic cotton', cert: 'eco-INSTITUT, OEKO-TEX', price: '$1,699-2,499' },
          { brand: 'My Green Mattress Natural Escape', material: 'Natural latex + organic cotton + organic wool + coils', cert: 'GOLS, GOTS, GREENGUARD Gold', price: '$1,399-1,999' },
          { brand: 'Casper Original (mainstream)', material: 'Polyurethane foam + polyester cover', cert: 'CertiPUR-US (low VOC) only', price: '$995-1,495' },
          { brand: 'Generic memory foam (Amazon, IKEA)', material: 'Polyurethane foam, often with flame retardants', cert: 'Often none', price: '$200-600' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What about the sheets and bedding?
      </h2>
      <p>
        Sheets, pillowcases, and duvet covers in polyester or polyester-cotton
        blend are continuous microfibre sources. Replace with:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>100% organic cotton sheets</strong> (Coyuchi, Boll &amp; Branch, Brooklinen Luxe)</li>
        <li><strong>100% linen sheets</strong> (MagicLinen, Cultiver, Parachute), naturally temperature-regulating</li>
        <li><strong>Bamboo viscose</strong> (Cariloha, Cozy Earth), semi-synthetic but plant-derived</li>
        <li><strong>Tencel / Lyocell</strong> (Brooklinen TENCEL, Sijo), wood-pulp-derived, closed-loop processing</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical bedroom transition (budget-tier)
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Day 1 (free):</strong> Run a HEPA air purifier in the bedroom 24/7.</li>
        <li><strong>Week 1 ($30-50):</strong> Replace polyester pillowcase with cotton or linen pillowcase.</li>
        <li><strong>Month 1 ($90-150):</strong> Replace polyester fibre pillow with buckwheat hulls, wool, or kapok.</li>
        <li><strong>Month 2 ($100-200):</strong> Replace polyester sheets with 100% cotton or linen.</li>
        <li><strong>Year 1 ($1,400-2,400):</strong> When your current mattress is at end-of-life (or sooner), replace with a certified natural latex + organic cotton + wool hybrid.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-clothing" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in clothing
        </Link>,{' '}
        <Link href="/blog/microplastics-in-the-air-at-home" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in the air at home
        </Link>, and{' '}
        <Link href="/blog/microplastics-and-thyroid" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics and thyroid function
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Material, stainless, glass, ceramic, cast iron, plastic (PE / PP / PS / PVC), silicone, wood.</>,
          <>Visible condition, scratches, chips, warping, fade.</>,
          <>Brand and product line, flags for non-stick / PFAS-treated items.</>,
          <>Use-context flags you log, heat exposure, dishwasher cycles, contact with hot or fatty food.</>,
          <>Cited published research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan kitchen and household products before buying"
        sub="Cookware, food storage, cutting boards, accessories. The app weighs material, condition, brand, and use-context to give a 0–100 risk score per item."
        buttonLabel="Scan household items in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do pillows and mattresses release microplastics?',
            answer:
              'Yes. Polyester fibre pillow fill sheds microfibres into your breathing zone for 8+ hours a night. Polyurethane foam mattresses are 100% plastic and off-gas VOCs for months to years. The 2017 Dris et al. study found textile fibres dominate indoor microplastic air load.',
          },
          {
            question: 'What is the safest mattress material?',
            answer:
              'Certified natural latex (GOLS), organic cotton (GOTS), and organic wool, typically combined in a hybrid with steel coils. Avocado Green, Naturepedic EOS Classic, Birch by Helix, and My Green Mattress Natural Escape are all evidence-supported choices.',
          },
          {
            question: 'Are memory foam mattresses dangerous?',
            answer:
              'Memory foam is viscoelastic polyurethane, a petroleum-based plastic. It off-gasses VOCs for months to years and may contain brominated flame retardants. CertiPUR-US certification verifies lower VOCs but doesn\'t make memory foam plastic-free. Natural latex is a healthier alternative.',
          },
          {
            question: 'What is the safest pillow fill?',
            answer:
              'Buckwheat hulls (Hullo), organic wool (Sleep & Beyond, Holy Lamb), kapok (Avocado Green), or natural latex shred. All release zero microplastic. Polyester fibre pillows (the most common) are the worst for nightly inhalation exposure.',
          },
          {
            question: 'Should I replace my mattress for microplastic reasons?',
            answer:
              'Not immediately, replace at end-of-life or when health concerns warrant it. In the meantime, run a HEPA air purifier in your bedroom and replace pillows and sheets with natural fibre alternatives. The mattress is the biggest investment but the highest single change.',
          },
          {
            question: 'What certifications should I look for?',
            answer:
              'GOLS (Global Organic Latex Standard), GOTS (Global Organic Textile Standard), GREENGUARD Gold (low VOC emissions), and MADE SAFE (no synthetic chemicals of concern). For latex specifically, verify "100% natural latex" not "natural latex blend".',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Dris R, Gasperi J, Mirande C, et al.',
            year: 2017,
            title: 'A first overview of textile fibers, including microplastics, in indoor and outdoor environments',
            publisher: 'Environmental Pollution',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0269749116307552',
          },
          {
            authors: 'Vianello A, Jensen RL, Liu L, Vollertsen J',
            year: 2019,
            title: 'Simulating human exposure to indoor airborne microplastics',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/s41598-019-45054-w',
          },
          {
            authors: 'US Consumer Product Safety Commission',
            year: 2007,
            title: '16 CFR 1633 - Standard for the flammability of mattresses',
            publisher: 'CPSC',
            url: 'https://www.cpsc.gov/Business--Manufacturing/Business-Education/Business-Guidance/Mattresses-and-Mattress-Foundations',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2024,
            title: 'Brominated flame retardants in consumer products',
            publisher: 'EFSA',
            url: 'https://www.efsa.europa.eu/en/topics/topic/brominated-flame-retardants',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
